/**
 * CookieConsent.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './CookieConsent.css';

const COOKIE_PREFERENCES_KEY = 'cookiePreferences';

const defaultPreferences = {
  necessary: true, // Always enabled
  analytics: false,
  marketing: false,
};

function getStoredPreferences() {
  try {
    const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (stored) return JSON.parse(stored);
  } catch { }
  return null;
}

export default function CookieConsent() {
  const { t } = useLanguage();
  const c = t.ui.cookies;
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    const stored = getStoredPreferences();
    if (!stored) {
      setShowBanner(true);
    } else {
      setPreferences(stored);
    }
  }, []);

  useEffect(() => {
    const handleOpenPreferences = () => {
      setShowBanner(true);
      setShowCustomize(true);
    };

    window.addEventListener('openCookiePreferences', handleOpenPreferences);
    return () => window.removeEventListener('openCookiePreferences', handleOpenPreferences);
  }, []);

  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true };
    setPreferences(all);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(all));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const none = { necessary: true, analytics: false, marketing: false };
    setPreferences(none);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(none));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    setShowBanner(false);
    setShowCustomize(false);
  };

  const handleCustomize = () => setShowCustomize(true);
  const handleBack = () => setShowCustomize(false);

  const handleCheckbox = (type) => (e) => {
    setPreferences((prev) => ({ ...prev, [type]: e.target.checked }));
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-consent-overlay" role="dialog" aria-modal="true" aria-label={c.aria}>
      <div className="cookie-consent-banner">
        {!showCustomize ? (
          <>
            <div className="cookie-consent-message">
              <strong>{c.bannerLead}</strong> {c.bannerRest}
            </div>
            <div className="cookie-consent-actions">
              <button className="cookie-btn accept" onClick={handleAcceptAll}>{c.acceptAll}</button>
              <button className="cookie-btn reject" onClick={handleRejectAll}>{c.rejectNonEssential}</button>
              <button className="cookie-btn customize" onClick={handleCustomize}>{c.customize}</button>
            </div>
          </>
        ) : (
          <div className="cookie-consent-customize">
            <h2>{c.title}</h2>
            <form>
              <div className="cookie-pref-item">
                <input type="checkbox" id="necessary" checked disabled />
                <label htmlFor="necessary"><strong>{c.necessary}</strong> {c.necessaryAlways}</label>
                <div className="cookie-pref-desc">{c.necessaryDesc}</div>
              </div>
              <div className="cookie-pref-item">
                <input type="checkbox" id="analytics" checked={preferences.analytics} onChange={handleCheckbox('analytics')} />
                <label htmlFor="analytics"><strong>{c.analytics}</strong></label>
                <div className="cookie-pref-desc">{c.analyticsDesc}</div>
              </div>
              <div className="cookie-pref-item">
                <input type="checkbox" id="marketing" checked={preferences.marketing} onChange={handleCheckbox('marketing')} />
                <label htmlFor="marketing"><strong>{c.marketing}</strong></label>
                <div className="cookie-pref-desc">{c.marketingDesc}</div>
              </div>
            </form>
            <div className="cookie-consent-actions">
              <button className="cookie-btn save" onClick={handleSavePreferences}>{c.save}</button>
              <button className="cookie-btn back" onClick={handleBack}>{c.back}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
